/*
 * @Description:维护管理->责任方维护
 * @Author: YuKi
 * @Date: 2024/11/09
 */
import React, { useCallback, memo, useState, useEffect } from 'react';
import { Button, Modal, Form, Input, Table, Row, Col, Space, message, Select } from 'antd';
import {
  getBusinessLinePage,
  editBusinessLine,
  delBusinessLine,
} from '@/api/maintenanceManagement/businessLineMaintenance';
import { ExclamationCircleOutlined, MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { ColumnsType } from 'antd/es/table';
import { RootStateOrAny, useSelector } from 'react-redux';
import { BusinessLineStatus } from '@/models/maintenanceManagement/businessLineMaintenance';
/* 列表字段类型定义 */
type GithubIssueItem = {
  id: number;
  businessLine: BusinessLineStatus /* 业务线 */;
  snList: string /* sn识别规则 */;
  createAccount: string /* 创建人 */;
  createDatetime: string /* 创建时间 */;
};

/* 列表查询参数 */
let searchParams = {
  pageSize: 10 /* 每页显示条数 */,
  pageIndex: 1 /* 当前页 */,
  businessLine: undefined /* 业务线 */,
  sysSn: undefined /* sysSn */,
};
let editId = '';
const BusinessLineMaintenance: React.FC = () => {
  const { t } = useTranslation();
  const { confirm } = Modal;
  /* 分页参数*/
  const [pagingRecord, setPagingRecord] = useState({ pageSize: 10, pageIndex: 1, total: 0 });
  let language = useSelector((state: RootStateOrAny) => state.get('app').get('language'));
  /* 列表源数据 */
  const [dataList, setDataList] = useState<GithubIssueItem[]>([]);
  /* table的loading */
  const [tableLoading, setTableLoading] = useState(false);
  /* 新增编辑弹窗打开关闭flag */
  const [isAEOpen, setIsAEOpen] = useState(false);
  /* 编辑弹窗的ref */
  const [aEForm] = Form.useForm();
  /* 查询表单的ref */
  const [searchForm] = Form.useForm();
  /* 新增编辑弹窗-> 区分新增编辑标志*/
  const [aEFlag, setAEFlag] = useState<string>('add');

  /* 列表字段 */
  const columns: ColumnsType<GithubIssueItem> = [
    /* 序号 */
    {
      title: t('common.serialNumber'),
      key: 'serialNumber',
      width: language === 'zh' || language === 'ja' ? 90 : 180,
      align: 'center',
      render: (text: any, record: any, index: any) => {
        return index + (pagingRecord.pageIndex - 1) * pagingRecord.pageSize + 1;
      },
    },
    /* SN识别规则 */
    {
      title: t('businessLineMaintenance.snRecognitionRules'),
      key: 'snList',
      dataIndex: 'snList',
      width: 240,
      align: 'center',
      ellipsis: true,
      render: (snList) => (snList ?? []).join('|'),
    },
    /* 所属业务线 */
    {
      title: t('businessLineMaintenance.businessLine'),
      key: 'businessLine',
      dataIndex: 'businessLine',
      width: 240,
      align: 'center',
      ellipsis: true,
      render: (sysbusinessLineSn) => {
        switch (sysbusinessLineSn) {
          case BusinessLineStatus.consumerBusiness:
            return t('businessLineMaintenance.consumerBusiness');
          case BusinessLineStatus.householdEnergyStorage:
            return t('businessLineMaintenance.householdEnergyStorage');
          case BusinessLineStatus.industrialAndCommercialEnergyStorage:
            return t('businessLineMaintenance.industrialAndCommercialEnergyStorage');
          case BusinessLineStatus.largeProjectEnergyStorage:
            return t('businessLineMaintenance.largeProjectEnergyStorage');
          default:
            return '';
        }
      },
    },
    /* 创建人 */
    {
      title: t('common.creator'),
      key: 'createAccount',
      dataIndex: 'createAccount',
      width: 120,
      align: 'center',
      ellipsis: true,
    },
    /* 创建时间 */
    {
      title: t('common.createTime'),
      key: 'createDatetime',
      dataIndex: 'createDatetime',
      width: 180,
      align: 'center',
      ellipsis: true,
    },
    /* 操作 */
    {
      title: t('common.operation'),
      key: 'option',
      align: 'center',
      width: 200,
      render: (_, record) => [
        <Space size="middle" key="space" wrap>
          {/* 编辑 */}
          <a key="edit" onClick={() => aEButton('edit', record)}>
            {t('common.editBtn')}
          </a>
          {/* 删除 */}
          <a key="delete" className="delete-text" onClick={() => deleteClick(record.id)}>
            {t('common.deleteBtn')}
          </a>
        </Space>,
      ],
    },
  ];

  /**
   * @Author: YuKi
   * @description: 获取列表数据
   * @return {*}
   */
  const getListInformation = () => {
    if (searchForm) {
      searchParams.businessLine = searchForm.getFieldValue('businessLine');
      searchParams.sysSn = searchForm.getFieldValue('sysSn');
    }
    setTableLoading(true);
    getBusinessLinePage(searchParams)
      .then((rep: any) => {
        if (rep && rep.code === 200 && rep.data && rep.data.rows) {
          setDataList([...rep.data.rows]);
          let paging = {
            pageIndex: rep.data.pageIndex,
            pageSize: rep.data.pageSize,
            total: rep.data.total,
          };
          setPagingRecord({ ...paging });
        } else {
          setDataList([]);
          let pagingInit = {
            pageIndex: 1,
            pageSize: 10,
            total: 0,
          };
          setPagingRecord({ ...pagingInit });
        }
        setTableLoading(false);
      })
      .catch((err) => {
        setDataList([]);
        let pagingInit = {
          pageIndex: 1,
          pageSize: 10,
          total: 0,
        };
        setPagingRecord({ ...pagingInit });
        setTableLoading(false);
      });
  };

  /**
   * @Author: YuKi
   * @description: 根据分页，初始化列表数据
   * @return {*}
   */
  useEffect(() => {
    searchParams = {
      pageSize: 10 /* 每页显示条数 */,
      pageIndex: 1 /* 当前页 */,
      businessLine: undefined /* 责任归属部门 */,
      sysSn: undefined /* 责任归属方 */,
    };
    pageChange(pagingRecord.pageIndex, pagingRecord.pageSize);
  }, []);

  /**
   * @Author: YuKi
   * @description: 分页的回调函数
   * @param {Number} currentPage 当前页
   * @param {Number} currentSize 页的总条数
   * @return {*}
   */
  const pageChange = useCallback((currentPage, currentSize) => {
    searchParams.pageIndex = currentPage === undefined ? searchParams.pageIndex : currentPage;
    searchParams.pageSize = currentSize === undefined ? searchParams.pageSize : currentSize;
    getListInformation();
  }, []);

  /**
   * @Author: YuKi
   * @description:查询列表
   * @param {Object} form 表单
   * @return {*}
   */
  const search = useCallback(() => {
    searchParams.pageIndex = 1;
    searchParams.pageSize = 10;
    getListInformation();
  }, []);

  /**
   * @Author: YuKi
   * @description: 删除
   * @param {string} id 序号
   * @return {*}
   */
  const deleteClick = (id) => {
    confirm({
      title: t('common.tip'),
      closable: true,
      okText: t('common.confirmBtn'),
      cancelText: t('common.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: t('common.deleteData'),
      onOk() {
        delBusinessLine(id).then((rep: any) => {
          if (rep && rep.code === 200) {
            message.success(t('common.operateSuccess')); /* 操作成功 */
            getListInformation();
          }
        });
      },
      onCancel() {
        message.info(t('common.operateCancel'));
      },
    });
  };

  /**
   * @Author: YuKi
   * @description: 新增/编辑弹窗->打开
   * @param {String} flag 'add':新增，'edit':编辑
   * @param {*} record 编辑的行数据
   * @return {*}
   */
  const aEButton = (flag, record?) => {
    editId = '';
    setAEFlag(flag);
    if (flag === 'edit') {
      aEForm.setFieldsValue({
        snList: record.snList ?? [undefined],
        businessLine: record.businessLine ?? undefined,
      });
      editId = record.id;
    }
    setIsAEOpen(true);
  };

  /**
   * @Author: YuKi
   * @description: 编辑弹窗确认
   * @return {*}
   */
  const handleAEOk = () => {
    aEForm
      .validateFields()
      .then((values) => {
        if (aEFlag === 'add') {
          editBusinessLine(values)
            .then((rep: any) => {
              if (rep && rep.code === 200) {
                setIsAEOpen(false);
                aEForm.resetFields();
                searchParams.pageIndex = 1;
                searchParams.pageSize = 10;
                getListInformation();
                message.success(t('common.addSuccess')); /* 新增成功 */
              }
            })
            .catch((err) => {});
        }
        if (aEFlag === 'edit') {
          editBusinessLine({ ...values, id: editId })
            .then((rep: any) => {
              if (rep && rep.code === 200) {
                setIsAEOpen(false);
                aEForm.resetFields();
                getListInformation();
                message.success(t('common.saveSuccess')); /* 保存成功 */
                editId = '';
              }
            })
            .catch((err) => {});
        }
      })
      .catch((errorInfo) => {});
  };

  /**
   * @Author: YuKi
   * @description: 编辑弹窗取消
   * @return {*}
   */
  const handleAECancel = () => {
    aEForm.resetFields();
    editId = '';
    setIsAEOpen(false);
  };

  return (
    <>
      <div className="custom-contant">
        <Form
          form={searchForm}
          name="search_form"
          layout="vertical"
          autoComplete="off"
          style={{ width: '100%' }}
          onFinish={search}
          className="custom-search in-line-style"
        >
          <Row gutter={12} align={'bottom'}>
            {/* SN识别规则 */}
            <Col xs={24} sm={8} md={8} lg={6} xl={6}>
              <Form.Item name="sysSn" label={t('businessLineMaintenance.snRecognitionRules')}>
                <Input placeholder={`${t('businessLineMaintenance.snRecognitionRulesPlaceholder')}`} allowClear />
              </Form.Item>
            </Col>
            {/* 所属业务线 */}
            <Col xs={24} sm={8} md={8} lg={6} xl={6}>
              <Form.Item name="businessLine" label={t('businessLineMaintenance.businessLine')}>
                <Select
                  placeholder={`${t('common.pleaseChoose')}`}
                  allowClear
                  options={[
                    {
                      value: BusinessLineStatus.householdEnergyStorage,
                      label: t('businessLineMaintenance.householdEnergyStorage'),
                    },
                    {
                      value: BusinessLineStatus.consumerBusiness,
                      label: t('businessLineMaintenance.consumerBusiness'),
                    },
                    {
                      value: BusinessLineStatus.industrialAndCommercialEnergyStorage,
                      label: t('businessLineMaintenance.industrialAndCommercialEnergyStorage'),
                    },
                    {
                      value: BusinessLineStatus.largeProjectEnergyStorage,
                      label: t('businessLineMaintenance.largeProjectEnergyStorage'),
                    },
                  ]}
                />
              </Form.Item>
            </Col>
            {/* 操作按钮 */}
            <Col xs={24} sm={8} md={8} lg={6} xl={6}>
              <Form.Item>
                <Space size="middle">
                  {/* 查询 */}
                  <Button type="primary" key="search" shape="round" htmlType="submit">
                    {t('common.searchBtn')}
                  </Button>
                  {/* 新增 */}
                  <Button type="primary" key="add" shape="round" onClick={() => aEButton('add')}>
                    {t('common.addBtn')}
                  </Button>
                </Space>
              </Form.Item>
            </Col>
          </Row>
        </Form>
        <Table
          columns={columns}
          loading={tableLoading}
          rowKey="id"
          pagination={{
            showQuickJumper: true,
            showSizeChanger: false,
            defaultCurrent: 1,
            current: pagingRecord.pageIndex,
            total: pagingRecord.total,
            pageSize: pagingRecord.pageSize,
            onChange: pageChange,
          }}
          dataSource={dataList}
          scroll={{ x: 1160, y: 'calc(100vh - 350px)' }}
          className="custom-table-flex auto-table"
        />
      </div>
      {/* 新增编辑弹窗 */}
      <Modal
        title={aEFlag === 'add' ? t('common.addBtn') : t('common.editBtn')}
        open={isAEOpen}
        onCancel={handleAECancel}
        maskClosable={false}
        footer={[
          /* 取消 */
          <Button key="back" shape="round" onClick={handleAECancel}>
            {t('common.cancelBtn')}
          </Button>,
          /* 保存 */
          <Button key="submit" shape="round" type="primary" onClick={handleAEOk}>
            {t('common.saveBtn')}
          </Button>,
        ]}
        forceRender
      >
        <Form name="aEForm" autoComplete="off" layout="vertical" form={aEForm} initialValues={{ snList: [undefined] }}>
          {/* SN识别规则 */}
          <Form.List name="snList">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => (
                  <div key={field.key} style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                    <Form.Item
                      {...field}
                      label={index == 0 ? t('businessLineMaintenance.snRecognitionRules') : ''}
                      validateTrigger="onBlur"
                      rules={[
                        {
                          required: true,
                          message: `${t('businessLineMaintenance.snRecognitionRulesRequire')}`,
                          whitespace: true,
                        },
                        {
                          validator: (rule, value) => {
                            const regex = /^[a-zA-Z0-9]{5}$/;
                            if (value && value.trim() && !regex.test(value.trim())) {
                              return Promise.reject(t('businessLineMaintenance.snRecognitionMatchRules'));
                            } else {
                              return Promise.resolve();
                            }
                          },
                        },
                      ]}
                      style={{ width: 'calc(100% - 50px)' }}
                    >
                      <Input placeholder={`${t('common.pleaseEnter')}`} allowClear />
                    </Form.Item>
                    {index === 0 && (
                      <Form.Item>
                        <Button type="primary" shape="circle" icon={<PlusOutlined />} onClick={() => add()}></Button>
                      </Form.Item>
                    )}
                    {index > 0 && (
                      <Form.Item>
                        <Button
                          type="primary"
                          shape="circle"
                          icon={<MinusOutlined />}
                          onClick={() => remove(field.name)}
                        ></Button>
                      </Form.Item>
                    )}
                  </div>
                ))}
              </>
            )}
          </Form.List>
          {/* 责任归属方 */}
          <Form.Item
            name="businessLine"
            validateTrigger="onBlur"
            label={t('businessLineMaintenance.businessLine')}
            rules={[
              {
                required: true,
                message: `${t('businessLineMaintenance.businessLineRequire')}`,
              },
            ]}
          >
            <Select
              placeholder={`${t('common.pleaseChoose')}`}
              allowClear
              options={[
                {
                  value: BusinessLineStatus.householdEnergyStorage,
                  label: t('businessLineMaintenance.householdEnergyStorage'),
                },
                {
                  value: BusinessLineStatus.consumerBusiness,
                  label: t('businessLineMaintenance.consumerBusiness'),
                },
                {
                  value: BusinessLineStatus.industrialAndCommercialEnergyStorage,
                  label: t('businessLineMaintenance.industrialAndCommercialEnergyStorage'),
                },
                {
                  value: BusinessLineStatus.largeProjectEnergyStorage,
                  label: t('businessLineMaintenance.largeProjectEnergyStorage'),
                },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default memo(BusinessLineMaintenance);
